// @flow
import React from 'react';
import PropTypes from 'prop-types';
import { intlShape } from 'react-intl';
import TemplateSelectionComponent from '../template-selection/TemplateSelectionComponent';
import Loading from '../../../common/loading/LoadingComponent';
import NewProductLayout from '../../common/new-product-layout/NewProductLayoutComponent';
import messages from '../../../../data/product-creation';
import { envUrl } from '../../../services/environment/EnvironmentService';
import { isProductTypeAvailable } from '../../../services/product-type/ProductTypeService';
import { getProductIdByRoute } from '../../../services/products-route/ProductsRouteService';
import { pushLocationAction } from '../../../core/actions/routing/RoutingActions';
import type {
  TemplateSelectionProps,
  TemplateSelectionActions,
} from '../template-selection/TemplateSelectionComponent';
import AnalyticsClient from '../../../../clients/AnalyticsClient';
import productEvents from '../../../../analytics/events/products';

type Actions = {
  clearSearch: () => void,
  configureCreationSettings: (Object, string) => void,
};

type Context = {
  intl: Object,
};

type DefaultProps = {
  params: {
    type: ?string,
  },
  parentLocation: string | null,
};

type Props = TemplateSelectionProps & {
  isLoading: boolean,
  isSingleProductType: boolean,
  actions: TemplateSelectionActions & Actions,
  runtimeConfig: {
    api: string,
  },
  match: Object,
  parentLocation: string | null,
  config: Object,
  location: {
    pathname: string,
  },
};

class CreationTemplateComponent extends React.Component<Props, Context> {
  static displayName = 'CreationTemplate';

  static contextTypes = {
    intl: intlShape.isRequired,
  };

  static defaultProps: DefaultProps = {
    params: {
      type: null,
    },
    parentLocation: null,
  };

  static propTypes = {
    templates: PropTypes.array.isRequired,
    start: PropTypes.number,
    isLoading: PropTypes.bool.isRequired,
    isSingleProductType: PropTypes.bool.isRequired,
    config: PropTypes.object.isRequired,
    actions: PropTypes.shape({
      clearSearch: PropTypes.func.isRequired,
      configureCreationSettings: PropTypes.func.isRequired,
      clearCreationTemplates: PropTypes.func.isRequired,
    }).isRequired,
    runtimeConfig: PropTypes.shape({
      api: PropTypes.string.isRequired,
    }).isRequired,
    params: PropTypes.shape({
      type: PropTypes.string,
    }),
    parentLocation: PropTypes.string,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,
  };

  UNSAFE_componentWillMount() {
    this.props.actions.clearCreationTemplates();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    this.redirectToProductCreation();

    if (nextProps.config !== this.props.config) {
      this.props.actions.configureCreationSettings(nextProps.config.productSizesAvailable, nextProps.runtimeConfig.api);
    }
  }

  redirectToProductCreation() {
    const { type } = this.props.match.params;

    if (type) {
      const availableSizes = this.props.config.productSizesAvailable;
      if (type && availableSizes && Object.keys(availableSizes).length) {
        const productTypeId = getProductIdByRoute(type);

        // Don't render the Template Selection Page if product type is not available to user
        if (!productTypeId || !isProductTypeAvailable(productTypeId, availableSizes)) {
          const redirectToProductSelection = pushLocationAction({ pathname: envUrl('/products/create') });
          this.props.history.transitionTo(redirectToProductSelection);
        }
      }
    }
  }

  onBackClick = (): void => {
    this.props.actions.clearSearch();
    AnalyticsClient.trackEvent(productEvents.backButtonNewProduct());
  };

  getTemplates() {
    const hasTemplates = this.props.templates.length > 0;
    if (this.props.isLoading && !hasTemplates) {
      return <Loading textLabel={this.context.intl.formatMessage(messages.loadingCreationTemplates)} />;
    }
    return <TemplateSelectionComponent {...this.props} />;
  }

  render() {
    return (
      <NewProductLayout
        parentLocation={this.props.parentLocation}
        onBackCallback={this.onBackClick}
        onCloseCallback={() => AnalyticsClient.trackEvent(productEvents.closeButtonNewProduct())}
      >
        {this.getTemplates()}
      </NewProductLayout>
    );
  }
}

export default CreationTemplateComponent;
